/**
 * src/components/LiveToolsControlPanel.tsx
 *
 * Broadcaster-only control surface for the GeoTrust Live Tools layer.
 * Wraps `liveToolsService` REST calls so the broadcaster can:
 *
 *   - Add a tool of any supported type (price_feed, price_chart,
 *     contract_function, trustline) before going live OR while live.
 *   - Edit an existing tool's display mode, position, refresh rate,
 *     and core config.
 *   - Hide / show / remove a tool without ending the session.
 *   - Force a value refresh for ad-hoc updates.
 *
 * The panel mounts independently of `LiveToolsLayer`.  Both share state
 * via the `liveToolsService` event bus, so a config change here
 * shows up in the layer (and on every viewer) without prop drilling.
 *
 * Required props: `sessionId` and `grantToken`.  Without them the panel
 * still renders but every mutating action is disabled — this is the
 * "configure tools before starting a live broadcast" path: the broadcaster
 * can lay them out, but they can't push to the server until they have
 * a paid-session grant.  When that arrives, mounting the panel with the
 * grant flushes any locally staged tools to the server.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addTool,
  applyLiveToolEvent,
  dispatchLiveToolEvent,
  getCapabilities,
  getSnapshot,
  listRegisteredContracts,
  removeTool,
  setVisibility,
  subscribeToLiveToolEvents,
  syncContractFromGeoLink,
  updateTool,
  refreshTool,
  type AddToolInput,
  type LiveTool,
  type LiveToolDisplayMode,
  type LiveToolEvent,
  type LiveToolPosition,
  type LiveToolType,
} from '../services/liveToolsService';
import { geolinkApi } from '../services/geolinkApi';
import { listEntities, type EntityRecord } from '../services/entitiesService';
import './LiveTools.css';

/**
 * Normalize a contract row coming back from GeoLink's `/api/contracts`
 * (the same endpoint the Smart Contracts tab uses) into the shape the
 * Live Tools picker already understands: `{ contractId, network, name,
 * discoveredFunctions, source }`.  Prefers the new `live_tools_metadata
 * .functions[]` block when present (it carries explicit `read_only`
 * flags + canonicalised types) and falls back to legacy
 * `discovered_functions`.
 */
function normalizeGeoLinkContract(row: any): any | null {
  if (!row) return null;
  const contractId: string = row.contract_address || row.contractId || '';
  if (!contractId) return null;
  const network: string = row.network || 'testnet';
  const lt = row.live_tools_metadata && Array.isArray(row.live_tools_metadata.functions)
    ? row.live_tools_metadata.functions
    : null;
  let discovered: any[] = [];
  if (lt) {
    discovered = lt;
  } else if (Array.isArray(row.discovered_functions)) {
    discovered = row.discovered_functions;
  } else if (row.discovered_functions && typeof row.discovered_functions === 'object') {
    discovered = Object.entries(row.discovered_functions).map(([name, meta]: [string, any]) => ({
      name, ...(meta && typeof meta === 'object' ? meta : {}),
    }));
  }
  return {
    contractId,
    network,
    name: row.contract_name || row.name || null,
    discoveredFunctions: discovered,
    source: 'geolink' as const,
  };
}

/** Merge local + GeoLink registries, dedupe by `network:contractId`,
 *  preferring whichever copy has more discovered functions (GeoLink
 *  metadata is richer when present, but a manually-synced local row
 *  may have been hand-edited). */
function mergeContracts(local: any[], geo: any[]): any[] {
  const byKey = new Map<string, any>();
  const push = (c: any) => {
    if (!c || !c.contractId) return;
    const key = `${c.network || 'testnet'}:${c.contractId}`;
    const existing = byKey.get(key);
    if (!existing) { byKey.set(key, c); return; }
    const a = (existing.discoveredFunctions || []).length;
    const b = (c.discoveredFunctions || []).length;
    byKey.set(key, b > a ? c : existing);
  };
  for (const c of (local || [])) push(c);
  for (const c of (geo || [])) push(c);
  return Array.from(byKey.values()).sort((a, b) =>
    String(a.name || a.contractId).localeCompare(String(b.name || b.contractId))
  );
}

/**
 * Translate the backend error codes that bubble up from the live-tools
 * REST surface into something a broadcaster can act on.  The codes
 * themselves come from `paidLiveSessionService.verifyGrant` (and a
 * handful of route-level rejections); leave anything we don't recognise
 * unchanged so it's visible in the UI for triage.
 */
function humanizePanelError(code: string | undefined | null): string {
  if (!code) return 'add_failed';
  switch (code) {
    case 'invalid_state':
      return 'Live session not ready yet — finish the wallet payment first.';
    case 'invalid_grant':
      return 'Live-session grant rejected — try ending the broadcast and starting again.';
    case 'grant_expired':
      return 'Live-session grant expired — end the broadcast and start a new one.';
    case 'not_found':
      return 'Live session has ended on the server — restart your broadcast.';
    case 'missing_grant_token':
      return 'Internal: missing grant token.  Reload the page and start the broadcast again.';
    default:
      return code;
  }
}

const DEFAULT_REFRESH_BY_TYPE: Record<LiveToolType, number> = {
  price_feed: 8,
  price_chart: 30,
  contract_function: 20,
  trustline: 20,
  // Slice 8 — drone tile refreshes fast, GCS slightly slower, airspace
  // is GeoLink-backed so cache TTL keeps it cheap.
  drone_telemetry: 5,
  gcs_status: 10,
  airspace_status: 60,
};

const DEFAULT_DISPLAY_BY_TYPE: Record<LiveToolType, LiveToolDisplayMode> = {
  price_feed: 'overlay_card',
  price_chart: 'chart_panel',
  contract_function: 'overlay_card',
  trustline: 'pinned_sidebar',
  drone_telemetry: 'overlay_card',
  gcs_status: 'compact_badge',
  airspace_status: 'pinned_sidebar',
};

const DEFAULT_POSITION_BY_TYPE: Record<LiveToolType, LiveToolPosition> = {
  price_feed: 'top_right',
  price_chart: 'sidebar_right',
  contract_function: 'top_left',
  trustline: 'sidebar_left',
  drone_telemetry: 'bottom_right',
  gcs_status: 'top_left',
  airspace_status: 'sidebar_right',
};

export interface LiveToolsControlPanelProps {
  sessionId: string | null | undefined;
  grantToken?: string | null;
  backendUrl?: string;
  /**
   * Wallet G-address of the broadcaster.  Used as the default
   * `accountId` when adding a `trustline` tool.  Optional — the
   * field is editable in the UI either way.
   */
  broadcasterWallet?: string | null;
  /**
   * Default network for `contract_function` registrations.  Defaults
   * to `'testnet'`.  Operators on mainnet should pass `'mainnet'`.
   */
  defaultNetwork?: 'testnet' | 'mainnet';
  /**
   * When set (including `[]`), the panel uses this list and does not
   * call `listEntities` internally — e.g. `ProfileOverlay` already loaded
   * wallet-linked drones / vehicles / GCS. Omit to fetch locally.
   */
  pilotEntities?: EntityRecord[];
}

interface Capabilities {
  types: LiveToolType[];
  displayModes: LiveToolDisplayMode[];
  positions: LiveToolPosition[];
  priceFeed: { defaultSymbols: string[]; supportedSymbols: string[]; defaultTtlMs: number };
  priceChart: { timeframes: string[] };
  contractFunction: { writePrefixesDenied: string[] };
}

const LiveToolsControlPanel: React.FC<LiveToolsControlPanelProps> = ({
  sessionId, grantToken, backendUrl, broadcasterWallet, defaultNetwork = 'testnet',
  pilotEntities: pilotEntitiesProp,
}) => {
  const [tools, setTools] = useState<LiveTool[]>([]);
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [registeredContracts, setRegisteredContracts] = useState<any[]>([]);
  const [pilotEntitiesLocal, setPilotEntitiesLocal] = useState<EntityRecord[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draftType, setDraftType] = useState<LiveToolType>('price_feed');
  const draftConfigRef = useRef<Record<string, any>>({});

  const opts = useMemo(() => ({ backend: backendUrl, grantToken: grantToken || '' }), [backendUrl, grantToken]);

  const pilotEntities = pilotEntitiesProp !== undefined
    ? pilotEntitiesProp
    : pilotEntitiesLocal;

  // Wallet-linked pilot entities — only when parent did not supply `pilotEntities`.
  useEffect(() => {
    if (pilotEntitiesProp !== undefined) return;
    const w = (broadcasterWallet || '').trim();
    if (!w) {
      setPilotEntitiesLocal([]);
      return;
    }
    let cancelled = false;
    listEntities({ wallet: w })
      .then((list) => {
        if (cancelled) return;
        setPilotEntitiesLocal(
          list.filter((e) =>
            e.entityType === 'drone'
            || e.entityType === 'vehicle'
            || e.entityType === 'ground_control_station'),
        );
      })
      .catch(() => { if (!cancelled) setPilotEntitiesLocal([]); });
    return () => { cancelled = true; };
  }, [broadcasterWallet, pilotEntitiesProp]);

  // Capabilities + registered contracts (read-only — open endpoints).
  //
  // Two sources feed the Contract function picker:
  //   1. The local Live Tools registry (tools the broadcaster manually
  //      synced via the "Sync from GeoLink" button below, or that any
  //      operator pre-seeded server-side).
  //   2. GeoLink's `/api/contracts` — the same endpoint the Smart
  //      Contracts panel uses, scoped to the connected wallet.  This is
  //      the source the user expects: anything they registered in the
  //      Smart Contracts tab should "just appear" here.
  // We merge both and dedupe by network:contractId, preferring whichever
  // copy carries the richer discovered-functions block.
  useEffect(() => {
    getCapabilities({ backend: backendUrl })
      .then((c) => setCaps(c as Capabilities))
      .catch(() => { /* ignore */ });

    let cancelled = false;
    Promise.allSettled([
      listRegisteredContracts({ backend: backendUrl }),
      geolinkApi.getContracts(),
    ]).then(([localResult, geoResult]) => {
      if (cancelled) return;
      const local = localResult.status === 'fulfilled' ? (localResult.value || []) : [];
      const geoRaw = geoResult.status === 'fulfilled' ? (geoResult.value || []) : [];
      const geo = geoRaw
        .map(normalizeGeoLinkContract)
        .filter((x): x is NonNullable<typeof x> => !!x);
      setRegisteredContracts(mergeContracts(local, geo));
    });
    return () => { cancelled = true; };
  }, [backendUrl]);

  // Subscribe + initial snapshot — same pattern as the layer.
  useEffect(() => {
    const unsub = subscribeToLiveToolEvents((ev: LiveToolEvent) => {
      if (sessionId && ev.sessionId && ev.sessionId !== sessionId) return;
      setTools((prev) => applyLiveToolEvent(prev, ev));
    });
    return unsub;
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) { setTools([]); return; }
    let cancelled = false;
    getSnapshot(sessionId, { backend: backendUrl })
      .then((snap) => { if (!cancelled) setTools(snap.tools || []); })
      .catch(() => { /* swallow */ });
    return () => { cancelled = true; };
  }, [sessionId, backendUrl]);

  const canMutate = !!(sessionId && grantToken);

  const handleAdd = useCallback(async () => {
    if (!sessionId) {
      setError('Live session not yet started — create a session first.');
      return;
    }
    if (!grantToken) {
      setError('Live-session grant required to add tools.');
      return;
    }
    setBusy('add');
    setError(null);
    try {
      const config = buildDefaultConfig(draftType, {
        broadcasterWallet,
        defaultSymbols: caps?.priceFeed.defaultSymbols || ['XLM', 'BTC', 'ETH'],
        defaultTimeframe: caps?.priceChart.timeframes?.[0] || '1m',
        defaultNetwork,
        registered: registeredContracts,
        ...draftConfigRef.current,
      });
      const input: AddToolInput = {
        type: draftType,
        config,
        displayMode: DEFAULT_DISPLAY_BY_TYPE[draftType],
        position: DEFAULT_POSITION_BY_TYPE[draftType],
        refreshIntervalSeconds: DEFAULT_REFRESH_BY_TYPE[draftType],
      };
      const created = await addTool(sessionId, input, opts as any);
      // Dispatch a synthetic event so both this panel and the
      // LiveToolsLayer react immediately.  Otherwise we'd be relying
      // on the WS `tool.added` echo from the WebRTC hub — and that
      // hub isn't connected until handleGoLive runs after the
      // 3-2-1 countdown, leaving pre-live tool additions invisible.
      dispatchLiveToolEvent({
        event: 'tool.added',
        sessionId,
        tool: created,
      });
      draftConfigRef.current = {};
    } catch (err: any) {
      setError(humanizePanelError(err?.message));
    } finally {
      setBusy(null);
    }
  }, [sessionId, grantToken, draftType, broadcasterWallet, caps, defaultNetwork, registeredContracts, opts]);

  const setDraftField = (key: string, value: any) => {
    draftConfigRef.current = { ...draftConfigRef.current, [key]: value };
  };

  return (
    <div className="live-tools-control-panel" data-testid="live-tools-control-panel">
      <h3>Live Tools</h3>

      {!canMutate && (
        <div style={{ fontSize: 11, opacity: 0.7 }}>
          {sessionId
            ? 'Live-session grant missing — start the broadcast to enable changes.'
            : 'Tools will activate once the live session starts.'}
        </div>
      )}

      <div className="panel-row">
        <select
          value={draftType}
          onChange={(e) => setDraftType(e.target.value as LiveToolType)}
        >
          <option value="price_feed">Price feed</option>
          <option value="price_chart">Price chart</option>
          <option value="contract_function">Contract function</option>
          <option value="trustline">Trustline</option>
          <option value="drone_telemetry">Drone telemetry</option>
          <option value="gcs_status">GCS status</option>
          <option value="airspace_status">Airspace status</option>
        </select>
        <DraftConfigFields
          type={draftType}
          caps={caps}
          broadcasterWallet={broadcasterWallet || ''}
          defaultNetwork={defaultNetwork}
          registered={registeredContracts}
          pilotEntities={pilotEntities}
          onChange={setDraftField}
        />
        <button className="primary" onClick={handleAdd} disabled={!canMutate || busy === 'add'}>
          {busy === 'add' ? 'Adding…' : 'Add tool'}
        </button>
      </div>
      {error && (
        <div className="tool-error-msg" style={{ marginTop: 8 }}>{error}</div>
      )}

      <div style={{ marginTop: 10 }}>
        {tools.length === 0 && <div style={{ fontSize: 11, opacity: 0.6 }}>No tools yet. Add one above.</div>}
        {tools.map((t) => (
          <ToolControlRow
            key={t.id}
            tool={t}
            sessionId={sessionId!}
            grantToken={grantToken!}
            backendUrl={backendUrl}
            canMutate={canMutate}
            caps={caps}
            onError={setError}
          />
        ))}
      </div>
    </div>
  );
};

export default LiveToolsControlPanel;

// ── Per-tool control row ───────────────────────────────────────────────────

interface ToolRowProps {
  tool: LiveTool;
  sessionId: string;
  grantToken: string;
  backendUrl?: string;
  canMutate: boolean;
  caps: Capabilities | null;
  onError: (msg: string | null) => void;
}

const ToolControlRow: React.FC<ToolRowProps> = ({
  tool, sessionId, grantToken, backendUrl, canMutate, caps, onError,
}) => {
  const [busy, setBusy] = useState<string | null>(null);
  const opts = { backend: backendUrl, grantToken };

  const handleField = async (patch: Parameters<typeof updateTool>[2]) => {
    if (!canMutate) return;
    setBusy('update');
    onError(null);
    try {
      const updated = await updateTool(sessionId, tool.id, patch, opts);
      dispatchLiveToolEvent({
        event: 'tool.updated',
        sessionId,
        tool: updated,
      });
    } catch (err: any) {
      onError(humanizePanelError(err?.message || 'update_failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleVisibility = async () => {
    if (!canMutate) return;
    setBusy('vis');
    onError(null);
    try {
      const updated = await setVisibility(sessionId, tool.id, !tool.visible, opts);
      // Use the precise lifecycle event so the reducer flips visibility
      // even on clients that don't track full tool diffs.
      dispatchLiveToolEvent({
        event: updated.visible ? 'tool.shown' : 'tool.hidden',
        sessionId,
        toolId: tool.id,
        tool: updated,
      });
    } catch (err: any) {
      onError(humanizePanelError(err?.message || 'visibility_failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async () => {
    if (!canMutate) return;
    setBusy('remove');
    onError(null);
    try {
      await removeTool(sessionId, tool.id, opts);
      dispatchLiveToolEvent({
        event: 'tool.removed',
        sessionId,
        toolId: tool.id,
      });
    } catch (err: any) {
      onError(humanizePanelError(err?.message || 'remove_failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleRefresh = async () => {
    if (!canMutate) return;
    setBusy('refresh');
    onError(null);
    try {
      const refreshed = await refreshTool(sessionId, tool.id, opts);
      if (refreshed) {
        dispatchLiveToolEvent({
          event: 'tool.value.updated',
          sessionId,
          toolId: tool.id,
          tool: refreshed,
        });
      }
    } catch (err: any) {
      onError(humanizePanelError(err?.message || 'refresh_failed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="live-tool-control-row">
      <div className="row-head">
        <span className="name">
          {tool.displayName} <span style={{ opacity: 0.5 }}>({tool.type})</span>
        </span>
        <span className={`tool-status ${tool.status}`}>{tool.status}</span>
      </div>
      <div className="row-controls">
        <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          Position
          <select
            value={tool.position}
            onChange={(e) => handleField({ position: e.target.value as LiveToolPosition })}
            disabled={!canMutate || busy !== null}
          >
            {(caps?.positions || []).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          Mode
          <select
            value={tool.displayMode}
            onChange={(e) => handleField({ displayMode: e.target.value as LiveToolDisplayMode })}
            disabled={!canMutate || busy !== null}
          >
            {(caps?.displayModes || []).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          Refresh (s)
          <input
            type="number"
            min={5}
            max={600}
            value={tool.refreshIntervalSeconds}
            onChange={(e) => handleField({ refreshIntervalSeconds: Number(e.target.value) || 15 })}
            disabled={!canMutate || busy !== null}
            style={{ width: 64 }}
          />
        </label>
        <button onClick={handleVisibility} disabled={!canMutate || busy !== null}>
          {tool.visible ? 'Hide' : 'Show'}
        </button>
        <button onClick={handleRefresh} disabled={!canMutate || busy !== null}>
          Refresh
        </button>
        <button className="danger" onClick={handleRemove} disabled={!canMutate || busy !== null}>
          Remove
        </button>
      </div>
    </div>
  );
};

// ── Draft config fields per type ───────────────────────────────────────────

const DraftConfigFields: React.FC<{
  type: LiveToolType;
  caps: Capabilities | null;
  broadcasterWallet: string;
  defaultNetwork: 'testnet' | 'mainnet';
  registered: any[];
  pilotEntities: EntityRecord[];
  onChange: (key: string, value: any) => void;
}> = ({ type, caps, broadcasterWallet, defaultNetwork, registered, pilotEntities, onChange }) => {
  const [symbols, setSymbols] = useState<string>(
    (caps?.priceFeed.defaultSymbols || ['XLM', 'BTC', 'ETH']).join(','),
  );
  const [chartSymbol, setChartSymbol] = useState('XLM');
  const [chartTimeframe, setChartTimeframe] = useState((caps?.priceChart.timeframes || ['1m'])[0]);
  const [accountId, setAccountId] = useState(broadcasterWallet || '');
  const [trustMode, setTrustMode] = useState<'all' | 'selected_asset' | 'gift_asset'>('all');
  const [selectedAsset, setSelectedAsset] = useState('');
  const [selectedIssuer, setSelectedIssuer] = useState('');
  const [contractKey, setContractKey] = useState<string>('');
  const [functionName, setFunctionName] = useState('');
  const [paramsJson, setParamsJson] = useState('{}');
  // Slice 8 — drone telemetry / gcs status / airspace status drafts.
  const [droneEntityId, setDroneEntityId] = useState('');
  const [gcsEntityIds, setGcsEntityIds] = useState('');
  const [airLat, setAirLat] = useState('');
  const [airLng, setAirLng] = useState('');
  const [airAlt, setAirAlt] = useState('');
  const [airRadius, setAirRadius] = useState('5000');

  useEffect(() => { onChange('symbols', symbols.split(',').map((s) => s.trim()).filter(Boolean)); }, [symbols, onChange]);
  useEffect(() => { onChange('symbol', chartSymbol); onChange('timeframe', chartTimeframe); }, [chartSymbol, chartTimeframe, onChange]);
  useEffect(() => {
    onChange('accountId', accountId);
    onChange('mode', trustMode);
    if (trustMode === 'selected_asset') {
      onChange('assetCode', selectedAsset);
      onChange('issuer', selectedIssuer || null);
    }
  }, [accountId, trustMode, selectedAsset, selectedIssuer, onChange]);
  useEffect(() => {
    if (!contractKey) return;
    const [network, contractId] = contractKey.split(':');
    onChange('network', network || defaultNetwork);
    onChange('contractId', contractId || '');
    onChange('functionName', functionName);
    try {
      onChange('parameters', paramsJson ? JSON.parse(paramsJson) : {});
    } catch {
      onChange('parameters', {});
    }
  }, [contractKey, functionName, paramsJson, defaultNetwork, onChange]);

  // Slice 8 — propagate the new tool drafts upward.
  useEffect(() => { onChange('entityId', droneEntityId.trim()); }, [droneEntityId, onChange]);
  useEffect(() => {
    onChange(
      'entityIds',
      gcsEntityIds.split(',').map((s) => s.trim()).filter(Boolean),
    );
  }, [gcsEntityIds, onChange]);
  useEffect(() => {
    onChange('lat',       Number(airLat));
    onChange('lng',       Number(airLng));
    onChange('altitudeM', airAlt    ? Number(airAlt)    : null);
    onChange('radiusM',   airRadius ? Number(airRadius) : 5000);
  }, [airLat, airLng, airAlt, airRadius, onChange]);

  if (type === 'price_feed') {
    return (
      <input
        value={symbols}
        onChange={(e) => setSymbols(e.target.value)}
        placeholder="Symbols (comma-separated)"
        title="e.g. XLM,BTC,ETH"
        style={{ minWidth: 160 }}
      />
    );
  }
  if (type === 'price_chart') {
    return (
      <>
        <input
          value={chartSymbol}
          onChange={(e) => setChartSymbol(e.target.value.toUpperCase())}
          style={{ width: 70 }}
          placeholder="XLM"
        />
        <select value={chartTimeframe} onChange={(e) => setChartTimeframe(e.target.value)}>
          {(caps?.priceChart.timeframes || ['1m', '5m', '15m', '1h', '4h', '1d']).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </>
    );
  }
  if (type === 'trustline') {
    return (
      <>
        <input
          value={accountId}
          onChange={(e) => setAccountId(e.target.value.trim())}
          placeholder="G… account"
          style={{ minWidth: 200 }}
        />
        <select value={trustMode} onChange={(e) => setTrustMode(e.target.value as any)}>
          <option value="all">All</option>
          <option value="selected_asset">Selected asset</option>
          <option value="gift_asset">Gift asset</option>
        </select>
        {trustMode === 'selected_asset' && (
          <>
            <input
              value={selectedAsset}
              onChange={(e) => setSelectedAsset(e.target.value)}
              placeholder="Code (e.g. USDC)"
              style={{ width: 90 }}
            />
            <input
              value={selectedIssuer}
              onChange={(e) => setSelectedIssuer(e.target.value)}
              placeholder="Issuer G… (omit for native)"
              style={{ minWidth: 200 }}
            />
          </>
        )}
      </>
    );
  }
  if (type === 'contract_function') {
    return (
      <>
        <select
          value={contractKey}
          onChange={(e) => setContractKey(e.target.value)}
        >
          <option value="">— select registered contract —</option>
          {registered.map((c) => (
            <option key={`${c.network}:${c.contractId}`} value={`${c.network}:${c.contractId}`}>
              {c.name || c.contractId} ({c.network}){c.source === 'geolink' ? ' · GeoLink' : ''}
            </option>
          ))}
        </select>
        <ContractFunctionPicker
          functions={
            registered.find((c) =>
              `${c.network}:${c.contractId}` === contractKey,
            )?.discoveredFunctions || []
          }
          functionName={functionName}
          onPickFunction={setFunctionName}
        />
        <input
          value={paramsJson}
          onChange={(e) => setParamsJson(e.target.value)}
          placeholder='{"user":"G…"}'
          style={{ minWidth: 180 }}
        />
        <SyncFromGeoLinkButton defaultNetwork={defaultNetwork} />
      </>
    );
  }
  if (type === 'drone_telemetry') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
        <input
          list="live-tools-drone-entity-pick"
          value={droneEntityId}
          onChange={(e) => setDroneEntityId(e.target.value.trim())}
          placeholder="entity id (wallet-linked drones / vehicles below)"
          style={{ minWidth: 220 }}
        />
        {pilotEntities.length > 0 && (
          <datalist id="live-tools-drone-entity-pick">
            {pilotEntities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.displayName} ({e.entityType})
              </option>
            ))}
          </datalist>
        )}
        {pilotEntities.length === 0 && (
          <span style={{ fontSize: 10, opacity: 0.65 }}>Register a test drone / vehicle (wallet-linked) to pick its id here.</span>
        )}
      </div>
    );
  }
  if (type === 'gcs_status') {
    const flying = pilotEntities.filter((e) => e.entityType === 'drone' || e.entityType === 'vehicle');
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
        <input
          value={gcsEntityIds}
          onChange={(e) => setGcsEntityIds(e.target.value)}
          placeholder="entity ids, comma-separated (optional — empty = all gateway links)"
          style={{ minWidth: 240 }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <button
            type="button"
            className="secondary"
            style={{ fontSize: 11, padding: '3px 8px' }}
            disabled={flying.length === 0}
            onClick={() => setGcsEntityIds(flying.map((e) => e.id).join(','))}
          >
            Use my drones & vehicles
          </button>
          <button
            type="button"
            className="secondary"
            style={{ fontSize: 11, padding: '3px 8px' }}
            disabled={pilotEntities.length === 0}
            onClick={() => setGcsEntityIds(pilotEntities.map((e) => e.id).join(','))}
          >
            Use all pilot entities
          </button>
          <button
            type="button"
            className="secondary"
            style={{ fontSize: 11, padding: '3px 8px' }}
            onClick={() => setGcsEntityIds('')}
          >
            Clear (all links)
          </button>
        </div>
      </div>
    );
  }
  if (type === 'airspace_status') {
    return (
      <>
        <input
          value={airLat}
          onChange={(e) => setAirLat(e.target.value)}
          placeholder="lat"
          style={{ width: 80 }}
        />
        <input
          value={airLng}
          onChange={(e) => setAirLng(e.target.value)}
          placeholder="lng"
          style={{ width: 80 }}
        />
        <input
          value={airAlt}
          onChange={(e) => setAirAlt(e.target.value)}
          placeholder="alt m"
          style={{ width: 70 }}
        />
        <input
          value={airRadius}
          onChange={(e) => setAirRadius(e.target.value)}
          placeholder="radius m"
          style={{ width: 80 }}
        />
      </>
    );
  }
  return null;
};

/**
 * Ad-hoc "register a contract by address" affordance.  Calls the
 * GeoTrust backend's `/registered-contracts/sync-from-geolink` route
 * which in turn reads GeoLink's `/api/contracts/by-address` and caches
 * the live_tools_metadata block locally — so the operator picks the
 * contract from a dropdown immediately afterward without pasting a
 * discovered_functions blob.
 */
const SyncFromGeoLinkButton: React.FC<{
  defaultNetwork: 'testnet' | 'mainnet';
}> = ({ defaultNetwork }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addr, setAddr] = useState('');
  const [net, setNet] = useState<'testnet' | 'mainnet'>(defaultNetwork);
  const [msg, setMsg] = useState<string | null>(null);
  const onSync = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const c = await syncContractFromGeoLink(addr.trim(), net);
      setMsg(`Synced ${c.name || c.contractId} (${c.discoveredFunctions?.length || 0} functions)`);
    } catch (err: any) {
      setMsg(err?.message || 'sync_failed');
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Pull contract metadata from GeoLink">
        + GeoLink contract
      </button>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <input
        value={addr}
        onChange={(e) => setAddr(e.target.value)}
        placeholder="C… contract id"
        style={{ minWidth: 180 }}
      />
      <select value={net} onChange={(e) => setNet(e.target.value as any)}>
        <option value="testnet">testnet</option>
        <option value="mainnet">mainnet</option>
      </select>
      <button className="primary" onClick={onSync} disabled={busy || !addr.trim()}>
        {busy ? 'Syncing…' : 'Sync'}
      </button>
      <button onClick={() => setOpen(false)}>Cancel</button>
      {msg && <span style={{ fontSize: 11, opacity: 0.8 }}>{msg}</span>}
    </span>
  );
};

/**
 * Render the discovered function list as a `<datalist>`-backed
 * <input>.  Functions classified as not read-only are shown but
 * disabled so the operator can see why they're missing — and so a
 * manual override has a clear "this won't work" signal.
 */
const ContractFunctionPicker: React.FC<{
  functions: Array<{ name: string; read_only?: boolean; returns?: string }>;
  functionName: string;
  onPickFunction: (n: string) => void;
}> = ({ functions, functionName, onPickFunction }) => {
  if (!functions || functions.length === 0) {
    return (
      <input
        value={functionName}
        onChange={(e) => onPickFunction(e.target.value)}
        placeholder="function_name"
      />
    );
  }
  const readable = functions.filter((f) => f.read_only !== false);
  return (
    <select
      value={functionName}
      onChange={(e) => onPickFunction(e.target.value)}
    >
      <option value="">— pick a read-only function —</option>
      {readable.map((f) => (
        <option key={f.name} value={f.name}>
          {f.name}{f.returns ? ` → ${f.returns}` : ''}
        </option>
      ))}
    </select>
  );
};

// ── Defaults builder ───────────────────────────────────────────────────────

function buildDefaultConfig(
  type: LiveToolType,
  ctx: any,
): any {
  if (type === 'price_feed') {
    const symbols = Array.isArray(ctx.symbols) && ctx.symbols.length > 0
      ? ctx.symbols
      : (ctx.defaultSymbols || ['XLM', 'BTC', 'ETH']);
    return { symbols };
  }
  if (type === 'price_chart') {
    return {
      symbol: ctx.symbol || 'XLM',
      timeframe: ctx.timeframe || ctx.defaultTimeframe || '1m',
      limit: 120,
    };
  }
  if (type === 'trustline') {
    return {
      accountId: ctx.accountId || ctx.broadcasterWallet || '',
      mode: ctx.mode || 'all',
      assetCode: ctx.assetCode || null,
      issuer: ctx.issuer || null,
    };
  }
  if (type === 'contract_function') {
    return {
      contractId: ctx.contractId || '',
      network: ctx.network || ctx.defaultNetwork || 'testnet',
      functionName: ctx.functionName || '',
      parameters: ctx.parameters || {},
    };
  }
  if (type === 'drone_telemetry') {
    return {
      entityId: String(ctx.entityId || '').trim(),
      ...(Array.isArray(ctx.fields) && ctx.fields.length > 0 ? { fields: ctx.fields } : {}),
    };
  }
  if (type === 'gcs_status') {
    return {
      entityIds: Array.isArray(ctx.entityIds) ? ctx.entityIds : [],
    };
  }
  if (type === 'airspace_status') {
    return {
      lat: Number(ctx.lat),
      lng: Number(ctx.lng),
      altitudeM: ctx.altitudeM != null ? Number(ctx.altitudeM) : null,
      radiusM:   ctx.radiusM   != null ? Number(ctx.radiusM)   : 5000,
    };
  }
  return {};
}
